import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Check, Film, Camera, Sparkles, Zap, Clock, Wand2, Maximize2, History, Gift, Play, SplitSquareHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MarketingManagerModal from "@/components/MarketingManagerModal";
import PhotographerModal from "@/components/PhotographerModal";
import ToolPromoCard from "@/components/ToolPromoCard";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import LandingToolsPricing from "@/components/LandingToolsPricing";
import SiteFooter from "@/components/SiteFooter";
import ProductInfoModal from "@/components/ProductInfoModal";

const PACKAGE_ICONS = {
    entry_ai: Sparkles,
    basic_ai: Zap,
    standard: Film,
    deluxe: Film,
    photography: Camera,
};

export default function Landing() {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [mmOpen, setMmOpen] = useState(false);
    const [photogOpen, setPhotogOpen] = useState(false);
    // Demo-toggle on the landing page: visitors can switch between an
    // interactive before/after slider and a recorded product video. Defaults
    // to "video" because autoplay shows progress in <2s — slider only reacts
    // when the user actually drags it.
    const [demoMode, setDemoMode] = useState("slider");
    const [exampleModal, setExampleModal] = useState(null);
    const [infoProduct, setInfoProduct] = useState(null);
    const [demoTool, setDemoTool] = useState("retouch");

    const DEMO_TOOLS = {
        retouch: {
            label: t("landing.ai_tools.feature_retouch_title"),
            video: "https://customer-assets.emergentagent.com/job_makelaar-booking/artifacts/dt0ozl02_Website%20demo%20video.m4v",
            before: "https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/ztuzvzpa_image-1790454810950.jpg",
            after: "https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/vcc33jsw_image-1790454813576.jpg",
        },
        outline: {
            label: t("landing.ai_tools.t3_title"),
            video: "https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/1729fdcd749f76c8_Outline%20en%20blur.mp4",
            before: "/demo/outline-before.jpg",
            after: "/demo/outline-after.jpg",
        },
        videolink: {
            label: t("landing.ai_tools.t2_title"),
            video: "https://vastgoed.social-factory.be/api/media/vtour_218ae5198a394d8fab670483665f071f.mp4",
        },
    };
    const activeDemo = DEMO_TOOLS[demoTool];

    const selectDemoTool = (k) => {
        setDemoTool(k);
        const d = DEMO_TOOLS[k];
        if (!d.video && demoMode === "video") setDemoMode("slider");
        if (!d.before && demoMode === "slider") setDemoMode("video");
    };

    useEffect(() => {
        const lang = (i18n.language || "nl").slice(0, 2).toLowerCase();
        api.get("/products", { params: { lang } }).then((r) => setProducts(r.data)).catch(() => {});
    }, [i18n.language]);

    const ctaDestination = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/register";

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body">
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                {/* Photo backdrop — Social Factory cameraman filming a villa.
                    Subject sits right, so a left→right white gradient keeps the
                    left-aligned headline readable on every screen size. */}
                <div className="absolute inset-0 bg-[#FAFAFA]" />
                <div
                    className="absolute inset-0 bg-no-repeat bg-cover bg-[position:38%_center] lg:bg-[length:150%_auto] lg:bg-[position:100%_top]"
                    style={{ backgroundImage: "url(https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/eb99vu8p_achtergrond%20platform.webp)" }}
                    data-testid="hero-bg-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#FAFAFA] via-[#FAFAFA]/85 to-[#FAFAFA]/45 lg:to-[#FAFAFA]/5" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FAFAFA]" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-24">
                    {/* Language switcher lives in the Navbar — no duplicate here. */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl"
                    >
                        <div className="flex items-center gap-3 mb-8">
                            <span className="h-px w-16 bg-[#E5A93D]" />
                            <span className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold">
                                {t("landing.tagline")}
                            </span>
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-8 text-balance">
                            {t("landing.hero_title_1")}
                            <br />
                            <span className="text-[#E5A93D] italic font-medium">{t("landing.hero_title_2")}</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-zinc-600 max-w-2xl mb-12 leading-relaxed">
                            {t("landing.hero_subtitle")}
                        </p>
                        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4">
                            <Button
                                onClick={() => navigate(ctaDestination)}
                                className="h-auto min-h-12 py-3 px-6 sm:px-8 w-full sm:w-auto whitespace-normal text-center leading-snug bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold tracking-wide rounded-sm active:scale-[0.98] group"
                                data-testid="hero-cta-start"
                            >
                                {user ? t("landing.cta_dashboard") : t("landing.cta_start_makelaar")}
                                <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-1" />
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() => document.getElementById("packages")?.scrollIntoView({ behavior: "smooth" })}
                                className="h-12 px-6 w-full sm:w-auto text-zinc-800 border border-zinc-300 hover:bg-zinc-100 rounded-sm"
                                data-testid="hero-cta-packages"
                            >
                                {t("landing.cta_packages")}
                            </Button>
                        </div>
                        {!user && (
                            <button
                                type="button"
                                onClick={() => setMmOpen(true)}
                                className="mt-4 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#7C3AED] transition-colors group"
                                data-testid="hero-cta-marketing-manager"
                            >
                                <span>{t("landing.cta_marketing_manager")}</span>
                                <span className="text-[#7C3AED] border-b border-dashed border-[#7C3AED]/40 group-hover:border-[#7C3AED]">{t("landing.more_info")}</span>
                                <ArrowRight className="h-3 w-3 text-[#7C3AED] transition-transform group-hover:translate-x-0.5" />
                            </button>
                        )}
                        {!user && (
                            <button
                                type="button"
                                onClick={() => setPhotogOpen(true)}
                                className="mt-2 block text-xs text-zinc-500 hover:text-[#0EA5E9] transition-colors"
                                data-testid="hero-cta-photographer"
                            >
                                Fotograaf? <span className="text-[#0EA5E9] border-b border-dashed border-[#0EA5E9]/40">Vraag hier je account aan</span>
                            </button>
                        )}
                    </motion.div>

                    {/* Tool promo card for logged-in users — anonymous visitors
                        see the AI tools section directly below the hero. */}
                    {user && user.role !== "admin" && (
                        <ToolPromoCard user={user} />
                    )}
                    {!user && (
                        <div
                            className="lg:absolute lg:top-24 lg:right-10 lg:w-[380px] mt-10 lg:mt-0 relative z-20"
                            data-testid="hero-tool-promo-anon"
                        >
                            <div className="relative overflow-hidden bg-gradient-to-br from-[#E5A93D]/[0.10] via-white/[0.03] to-transparent border border-[#E5A93D]/30 hover:border-[#E5A93D]/55 transition-colors rounded-sm p-6 backdrop-blur-sm">
                                <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#E5A93D]/25 blur-3xl pointer-events-none" />
                                <div className="relative">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Sparkles className="h-3.5 w-3.5 text-[#E5A93D]" />
                                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#E5A93D] font-bold">
                                            {t("landing.ai_tools.eyebrow")}
                                        </span>
                                    </div>
                                    <h3 className="font-display text-2xl font-bold tracking-tight leading-tight mb-3 text-zinc-950">
                                        {t("landing.ai_tools.promo_title_1")}{" "}
                                        <span className="text-[#E5A93D] italic font-medium">{t("landing.ai_tools.promo_highlight")}</span>{" "}
                                        {t("landing.ai_tools.promo_title_2")}
                                    </h3>
                                    <p className="text-xs text-zinc-500 leading-relaxed mb-4">
                                        {t("landing.ai_tools.promo_subtitle")}
                                    </p>
                                    <div className="space-y-1.5 mb-5">
                                        {[
                                            { Icon: Wand2, label: t("landing.ai_tools.feature_retouch_title"), accent: "#E5A93D" },
                                            { Icon: Film, label: t("landing.ai_tools.t2_title"), accent: "#7C3AED" },
                                            { Icon: SplitSquareHorizontal, label: t("landing.ai_tools.t3_title"), accent: "#22c55e" },
                                        ].map((tool) => (
                                            <div
                                                key={tool.label}
                                                className="flex items-center gap-2.5 text-[13px] px-3 py-2 bg-white border border-zinc-200 rounded-sm text-zinc-700 font-medium"
                                            >
                                                <tool.Icon className="h-3.5 w-3.5 shrink-0" style={{ color: tool.accent }} />
                                                <span>{tool.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/register")}
                                        data-testid="hero-tool-promo-anon-cta"
                                        className="group inline-flex items-center justify-center gap-2 w-full h-11 px-4 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-[0.98]"
                                    >
                                        <Sparkles className="h-4 w-4" />
                                        <span>{t("landing.ai_tools.free_credits_cta")}</span>
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const el = document.getElementById("ai-tools");
                                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                        }}
                                        data-testid="hero-tool-promo-more-info"
                                        className="group mt-2 inline-flex items-center justify-center gap-1.5 w-full h-9 px-3 text-xs font-medium text-zinc-600 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-400 bg-white hover:bg-zinc-100 rounded-sm transition"
                                    >
                                        <span>{t("landing.ai_tools.more_info_btn")}</span>
                                        <ArrowRight className="h-3 w-3 rotate-90 transition-transform group-hover:translate-y-0.5" />
                                    </button>
                                    <p className="mt-3 text-[10px] text-zinc-500 text-center">
                                        {t("landing.ai_tools.free_credits_desc").split(".")[0]}.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* AI TOOLS — directly below the hero so visitors immediately see
                the AI Photo Retouch / Video-from-link / Outline & blur tiles. */}
            <section id="ai-tools" className="relative py-14 lg:py-20 overflow-hidden" data-testid="ai-tools-section">
                <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF] via-[#FAFAFA] to-[#FFFFFF]" />
                <div
                    className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full blur-3xl opacity-20"
                    style={{ background: "#E5A93D" }}
                />
                <div
                    className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full blur-3xl opacity-15"
                    style={{ background: "#7C3AED" }}
                />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-4 inline-flex items-center gap-2">
                        <Wand2 className="h-3.5 w-3.5" /> {t("landing.ai_tools.eyebrow")}
                    </div>
                    <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95] mb-6 max-w-3xl text-balance">
                        {t("landing.ai_tools.title_1")}{" "}
                        <span className="italic font-medium text-[#E5A93D]">{t("landing.ai_tools.title_highlight")}</span>{" "}
                        {t("landing.ai_tools.title_2")}
                    </h2>
                    <p className="text-zinc-500 max-w-2xl text-lg mb-8 leading-relaxed">
                        {t("landing.ai_tools.subtitle")}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                        {[
                            { Icon: Wand2, title: t("landing.ai_tools.feature_retouch_title"), desc: t("landing.ai_tools.feature_retouch_desc"), accent: "#E5A93D", bullets: t("landing.ai_tools.t1_bullets", { returnObjects: true }), example: "retouch" },
                            { Icon: Film, title: t("landing.ai_tools.t2_title"), desc: t("landing.ai_tools.t2_desc"), accent: "#7C3AED", bullets: t("landing.ai_tools.t2_bullets", { returnObjects: true }), example: "video" },
                            { Icon: SplitSquareHorizontal, title: t("landing.ai_tools.t3_title"), desc: t("landing.ai_tools.t3_desc"), accent: "#22c55e", bullets: t("landing.ai_tools.t3_bullets", { returnObjects: true }), example: null },
                        ].map((f, i) => (
                            <motion.div
                                key={f.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.1 }}
                                className="group relative bg-white hover:bg-zinc-100 border border-zinc-200 hover:border-zinc-400 rounded-sm p-6 transition-all"
                                data-testid={`ai-tool-feature-${i}`}
                            >
                                <div
                                    className="inline-flex items-center justify-center h-11 w-11 rounded-sm bg-zinc-100 mb-4"
                                    style={{ color: f.accent }}
                                >
                                    <f.Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
                                <p className="text-sm text-zinc-500 leading-relaxed mb-4">{f.desc}</p>
                                <ul className="space-y-1.5 mb-4">
                                    {(f.bullets || []).map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-[13px] text-zinc-600">
                                            <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: f.accent }} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                {f.example && (
                                    <button
                                        type="button"
                                        onClick={() => setExampleModal(f.example)}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-sm border transition active:scale-95"
                                        style={{ color: f.accent, borderColor: `${f.accent}55`, backgroundColor: `${f.accent}0d` }}
                                        data-testid={`example-btn-${f.example}`}
                                    >
                                        {t("landing.ai_tools.example_btn")}
                                    </button>
                                )}
                            </motion.div>
                        ))}
                    </div>

                    {/* LIVE DEMO — visitors toggle between an interactive
                        before/after slider and an autoplay product video.
                        Two distinct demos covering different learning styles:
                        the slider proves the AI works on a single photo, the
                        video shows the full platform flow. */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mb-12"
                        data-testid="landing-ai-demo"
                    >
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#22c55e] font-bold">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e]" />
                                </span>
                                {t("landing.ai_tools.demo_live_badge")}
                            </div>
                            <div className="inline-flex flex-wrap items-center gap-0.5 rounded-sm bg-zinc-100 border border-zinc-200 p-0.5" role="group" aria-label="Demo tool">
                                {Object.entries(DEMO_TOOLS).map(([k, d]) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => selectDemoTool(k)}
                                        aria-pressed={demoTool === k}
                                        data-testid={`demo-tool-${k}`}
                                        className={`h-9 px-3 text-[11px] uppercase tracking-wider font-bold rounded-sm transition ${
                                            demoTool === k ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-950 hover:bg-white"
                                        }`}
                                    >
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                            <div className="inline-flex items-center gap-0.5 rounded-sm bg-black/40 backdrop-blur-md border border-white/10 ml-auto" role="group" aria-label="Demo type">
                                {activeDemo.video && (
                                <button
                                    type="button"
                                    onClick={() => setDemoMode("video")}
                                    aria-pressed={demoMode === "video"}
                                    data-testid="demo-toggle-video"
                                    className={`inline-flex items-center gap-1.5 h-9 px-3 text-[11px] uppercase tracking-wider font-bold rounded-sm transition ${
                                        demoMode === "video"
                                            ? "bg-[#E5A93D] text-black"
                                            : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                                    }`}
                                >
                                    <Play className="h-3 w-3" /> Video
                                </button>
                                )}
                                {activeDemo.before && (
                                <button
                                    type="button"
                                    onClick={() => setDemoMode("slider")}
                                    aria-pressed={demoMode === "slider"}
                                    data-testid="demo-toggle-slider"
                                    className={`inline-flex items-center gap-1.5 h-9 px-3 text-[11px] uppercase tracking-wider font-bold rounded-sm transition ${
                                        demoMode === "slider"
                                            ? "bg-[#E5A93D] text-black"
                                            : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                                    }`}
                                >
                                    <SplitSquareHorizontal className="h-3 w-3" /> Voor / na slider
                                </button>
                                )}
                            </div>
                        </div>

                        {demoMode === "video" && activeDemo.video ? (
                            <div className="relative rounded-sm overflow-hidden border border-zinc-200 bg-zinc-50" data-testid="demo-video-wrap">
                                <video
                                    key={`demo-video-${demoTool}`}
                                    src={activeDemo.video}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    preload="metadata"
                                    data-testid="landing-demo-video"
                                    className="w-full h-auto max-h-[78vh] block"
                                >
                                    Je browser ondersteunt geen video — vraag een demo via Cyril.
                                </video>
                            </div>
                        ) : (
                            <div className="relative rounded-sm overflow-hidden border border-zinc-200 bg-zinc-50 flex items-center justify-center p-2 sm:p-4" data-testid="demo-slider-wrap">
                                <BeforeAfterSlider
                                    key={`demo-slider-${demoTool}`}
                                    beforeUrl={activeDemo.before}
                                    afterUrl={activeDemo.after}
                                    alt="Voorbeeld voor en na AI"
                                    className="max-h-[70vh]"
                                    testId="landing-demo-slider"
                                />
                            </div>
                        )}

                        {demoMode === "slider" && (
                            <p className="mt-3 text-xs text-zinc-500 flex items-center gap-2">
                                <ArrowRight className="h-3 w-3 -rotate-180" />
                                {t("landing.ai_tools.demo_hint")}
                                <ArrowRight className="h-3 w-3" />
                            </p>
                        )}
                    </motion.div>

                    {/* Free-credits CTA — only visible to anonymous visitors;
                        logged-in users already have their credits. */}
                    {!user && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="relative overflow-hidden bg-gradient-to-br from-[#E5A93D]/[0.12] via-white to-[#7C3AED]/[0.08] border border-[#E5A93D]/30 rounded-md p-8 lg:p-12"
                            data-testid="free-credits-cta"
                        >
                            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                                <div className="flex-1 min-w-0">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm bg-[#E5A93D] text-black text-[10px] font-black tracking-wider mb-4">
                                        <Gift className="h-3 w-3" /> {t("landing.ai_tools.free_credits_badge")}
                                    </span>
                                    <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-3 text-balance">
                                        {t("landing.ai_tools.free_credits_title")}
                                    </h3>
                                    <p className="text-zinc-600 max-w-xl leading-relaxed">
                                        {t("landing.ai_tools.free_credits_desc")}
                                    </p>
                                </div>
                                <Button
                                    onClick={() => navigate("/register")}
                                    className="h-12 px-6 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold tracking-wide rounded-sm active:scale-[0.98] whitespace-nowrap shadow-lg shadow-[#E5A93D]/20"
                                    data-testid="free-credits-cta-button"
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    {t("landing.ai_tools.free_credits_cta")}
                                </Button>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* TOOLS PRICING — public credit packs + usage table */}
            <LandingToolsPricing />

            {/* PACKAGES - BENTO GRID */}
            <section id="packages" className="relative py-14 lg:py-20" data-testid="packages-section">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mb-10 grid grid-cols-3 gap-8 border-b border-zinc-200 pb-8"
                    >
                        {[
                            { v: "5", l: t("landing.stats.formules_label") },
                            { v: t("landing.stats.response_value"), l: t("landing.stats.response_label") },
                            { v: "4K", l: t("landing.stats.quality_label") },
                        ].map((s, i) => (
                            <div key={i}>
                                <div className="font-display font-black text-4xl text-zinc-950">{s.v}</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">{s.l}</div>
                            </div>
                        ))}
                    </motion.div>

                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-4">{t("landing.packages.eyebrow")}</div>
                            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none max-w-2xl text-balance">
                                {t("landing.packages.title_1")} <span className="italic font-medium text-[#E5A93D]">{t("landing.packages.title_ai")}</span> {t("landing.packages.title_2")}
                            </h2>
                        </div>
                        <p className="text-zinc-500 max-w-md">
                            {t("landing.packages.subtitle")}
                        </p>
                    </div>

                    {!user && (
                        <div className="mb-8 p-5 bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md flex items-center justify-between gap-4 flex-wrap" data-testid="public-ai-pricing-banner">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D] mb-1">{t("landing.ai_tools.free_credits_badge")}</div>
                                <div className="text-sm text-zinc-600">
                                    <span className="font-semibold text-zinc-950">{t("landing.ai_tools.pricing_line_retouch")}</span>
                                    <span className="mx-2 text-zinc-300">·</span>
                                    <span className="font-semibold text-zinc-950">{t("landing.ai_tools.pricing_line_video")}</span>
                                    <span className="ml-2 text-xs text-zinc-500">{t("landing.ai_tools.pricing_line_note")}</span>
                                </div>
                            </div>
                            <Button
                                onClick={() => navigate("/register")}
                                className="h-10 px-5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                                data-testid="banner-register-btn"
                            >
                                {t("landing.packages.create_account")}
                            </Button>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map((p, i) => {
                            const Icon = PACKAGE_ICONS[p.id] || Film;

                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.06 }}
                                    className="group relative bg-[#FFFFFF] border border-zinc-100 rounded-md overflow-hidden hover:border-zinc-400 hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer"
                                    onClick={() => setInfoProduct(p)}
                                    data-testid={`package-${p.id}-card`}
                                >
                                    <div className="h-48 md:h-56 bg-cover bg-center relative overflow-hidden bg-zinc-50">
                                        {p.thumbnail_url && p.thumbnail_type === "video" ? (
                                            <video
                                                src={p.thumbnail_url}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                preload="metadata"
                                                className="absolute inset-0 w-full h-full object-cover"
                                                data-testid={`package-${p.id}-thumbnail-video`}
                                            />
                                        ) : p.thumbnail_url ? (
                                            <div
                                                className="absolute inset-0 bg-cover bg-center"
                                                style={{ backgroundImage: `url(${p.thumbnail_url})` }}
                                                data-testid={`package-${p.id}-thumbnail-image`}
                                            />
                                        ) : (
                                            <div className="absolute inset-0 bg-gradient-to-br from-[#F4F4F5] to-[#FFFFFF]" />
                                        )}
                                        {p.thumbnail_url ? (
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                                        ) : (
                                            <div className="absolute inset-x-0 bottom-0 h-px bg-zinc-200" />
                                        )}
                                        <div className="absolute top-4 right-4 flex items-center gap-1.5">
                                            {p.ai_badge && (
                                                <div className="bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-testid={`ai-badge-${p.id}`}>
                                                    AI
                                                </div>
                                            )}
                                            {p.featured && (
                                                <div className="bg-[#E5A93D] text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
                                                    {t("landing.packages.premium")}
                                                </div>
                                            )}
                                        </div>
                                        <div className="absolute bottom-4 left-6 flex items-center gap-2">
                                            <Icon className="h-4 w-4 text-[#E5A93D]" />
                                            <span className={`text-xs uppercase tracking-[0.25em] ${p.thumbnail_url ? "text-white" : "text-zinc-700"}`}>{p.name}</span>
                                        </div>
                                    </div>
                                    <div className="p-6 lg:p-8">
                                        <h3 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-zinc-950 mb-2">
                                            {p.tagline}
                                        </h3>
                                        <p className="text-sm text-zinc-500 leading-relaxed mb-2">{p.description}</p>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setInfoProduct(p); }}
                                            className="text-xs font-bold text-[#C8922C] hover:text-[#E5A93D] underline underline-offset-4 mb-6"
                                            data-testid={`package-${p.id}-more-info`}
                                        >
                                            {t("landing.packages.more_info")} +
                                        </button>

                                        <div className="space-y-2 mb-6">
                                            {p.deliverables.map((d, idx) => (
                                                <div key={idx} className="flex items-start gap-2 text-sm text-zinc-600">
                                                    <Check className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                                    <span>{d}</span>
                                                </div>
                                            ))}
                                            {p.duration && (
                                                <div className="flex items-start gap-2 text-sm text-zinc-500">
                                                    <Check className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                                    <span>{p.duration}</span>
                                                </div>
                                            )}
                                            {p.delivery_time && (
                                                <div className="flex items-start gap-2 text-sm text-zinc-500" data-testid={`package-${p.id}-delivery`}>
                                                    <Clock className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                                    <span>{p.delivery_time}</span>
                                                </div>
                                            )}
                                        </div>

                                        {p.options && p.options.length > 0 && (
                                            <div className="border-t border-zinc-100 pt-4 mb-6">
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("landing.packages.extra_options")}</div>
                                                {p.options.map((o) => (
                                                    <div key={o.id} className="flex justify-between text-xs text-zinc-500 py-1">
                                                        <span>{o.label}</span>
                                                        <span className="text-zinc-600">{`+€${o.price}`}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="flex items-end justify-between pt-4 border-t border-zinc-100">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.price_from ? t("landing.packages.price_from_label") : t("landing.packages.price_label")}</div>
                                                <div className="font-display font-black text-3xl text-[#E5A93D]">
                                                    {p.price_from && <span className="text-base font-medium text-zinc-500 mr-1">{t("landing.packages.from")}</span>}€{p.price}
                                                </div>
                                                {user && p.photo_tiers && (
                                                    <div className="text-[10px] text-zinc-500 mt-1">
                                                        {p.photo_tiers.map((tier) => `${tier.label}: €${tier.price}`).join(" · ")}
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                onClick={(e) => { e.stopPropagation(); setInfoProduct(p); }}
                                                variant="ghost"
                                                className="text-zinc-950 hover:text-[#E5A93D] hover:bg-transparent px-0"
                                                data-testid={`package-${p.id}-select`}
                                            >
                                                {t("landing.packages.more_info")} <ArrowRight className="ml-1 h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
                {infoProduct && (
                    <ProductInfoModal
                        product={infoProduct}
                        onClose={() => setInfoProduct(null)}
                        onRequest={() => { setInfoProduct(null); navigate(user ? "/new-request" : "/register"); }}
                    />
                )}
            </section>

            {/* WORKFLOW — placed directly under the video packages so users
                first see WHAT they can order, then HOW the process flows. */}
            <section className="py-14 lg:py-20 bg-[#FFFFFF] border-y border-zinc-100">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-4">{t("landing.workflow.eyebrow")}</div>
                    <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none mb-10 max-w-3xl text-balance">
                        {t("landing.workflow.title")}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {[
                            { n: "01", t: t("landing.workflow.step1_title"), d: t("landing.workflow.step1_desc") },
                            { n: "02", t: t("landing.workflow.step2_title"), d: t("landing.workflow.step2_desc") },
                            { n: "03", t: t("landing.workflow.step3_title"), d: t("landing.workflow.step3_desc") },
                            { n: "04", t: t("landing.workflow.step4_title"), d: t("landing.workflow.step4_desc") },
                        ].map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.1 }}
                            >
                                <div className="font-display font-black text-5xl text-[#E5A93D]/30 mb-4">{s.n}</div>
                                <div className="text-lg font-bold text-zinc-950 mb-2">{s.t}</div>
                                <div className="text-sm text-zinc-500 leading-relaxed">{s.d}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>


            {/* CTA */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-6 text-balance">
                        {t("landing.cta_section.title")}
                    </h2>
                    <p className="text-zinc-500 mb-10 text-lg">
                        {t("landing.cta_section.subtitle")}
                    </p>
                    <Link to={ctaDestination}>
                        <Button
                            className="h-12 px-8 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                            data-testid="footer-cta-start"
                        >
                            {user ? t("landing.cta_section.cta_dashboard") : t("landing.cta_section.cta_register")}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </Link>
                </div>
            </section>

            <SiteFooter />

            <MarketingManagerModal open={mmOpen} onClose={() => setMmOpen(false)} />
            <PhotographerModal open={photogOpen} onClose={() => setPhotogOpen(false)} />

            {exampleModal && (
                <div
                    className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setExampleModal(null)}
                    data-testid="example-modal"
                >
                    <div
                        className="relative bg-white rounded-md p-4 sm:p-6 max-w-4xl w-full max-h-[90vh] overflow-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-4 mb-4">
                            <h3 className="font-display font-bold text-lg">
                                {exampleModal === "video" ? t("landing.ai_tools.video_modal_title") : t("landing.ai_tools.retouch_modal_title")}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setExampleModal(null)}
                                className="h-8 w-8 flex items-center justify-center rounded-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-600"
                                data-testid="example-modal-close"
                            >
                                ✕
                            </button>
                        </div>
                        {exampleModal === "video" ? (
                            <video
                                src="https://vastgoed.social-factory.be/api/media/vtour_218ae5198a394d8fab670483665f071f.mp4"
                                controls
                                autoPlay
                                muted
                                playsInline
                                className="w-full max-h-[72vh] rounded-sm bg-black"
                                data-testid="example-video"
                            />
                        ) : (
                            <BeforeAfterSlider
                                beforeUrl="https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/ztuzvzpa_image-1790454810950.jpg"
                                afterUrl="https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/vcc33jsw_image-1790454813576.jpg"
                                alt="Voorbeeld voor en na AI"
                                className="max-h-[72vh]"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
